import { encodeBase64 } from '@schooly/utils/encode-base64';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';

import { SCHOOLY_API_V2_URL } from '../config';
import { DEFAULT_DATE_FORMAT_FNS } from '../constants';
import {
  CheckUserEmailInUseForSchoolRequest,
  CheckUserEmailInUseForSchoolResponse,
  CheckUserEmailInUsePublicRequest,
  CheckUserEmailInUsePublicRequestQuery,
  CheckUserEmailInUsePublicResponse,
  CreateUnverifiedEmailPublicRequest,
  CreateUnverifiedEmailPublicResponse,
  CreateUnverifiedEmailResponse,
  ListProfileArguments,
  ListProfileReturn,
  SaveFilterResponse,
  UserParentResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
} from './apiTypes/endpoints/people';
import {
  ApiError,
  EmploymentHistoryEntry,
  PagedResponse,
  SORT_DIRECTION,
  StringOrNull,
  UserType,
} from './apiTypes/misc';
import {
  createUnverifiedEmailPath,
  createUnverifiedEmailPublicPath,
  emailInUseForSchoolPath,
  emailInUsePublicPath,
  userParentPath,
  verifyEmailPath,
} from './apiTypes/paths/people';
import {
  RQUseInfiniteQueryOptions,
  RQUseMutationOptions,
  RQUseMutationResult,
  RQUseQueryOptions,
} from './apiTypes/query';
import {
  ExportStudentsParams,
  ExportStudentsQuery,
  getExportStudentsPath,
} from './apiTypes/students';
import { UserRolePermissions } from './apiTypes/userRoles';
import {
  FilterKeys,
  FilterSection,
  NewAdult,
  NewFamilyMember,
  SavedFilter,
  SyncUser,
} from './apiTypes/users';
import { ProductTypeId } from './invoices';
import * as api from './requests';
import { getSortParams } from './utils/getSortParam';
import { removeObjectEmptyArrayValues } from './utils/removeObjectEmptyArrayValues';
import { removeObjectFalsyValues } from './utils/removeObjectFalsyValues';
import { removeObjectUndefinedNullValues } from './utils/removeObjectUndefinedNullValues';
import { User } from './generated/schooly-api-v1/UserRoute';

const SYNC_URL = '/user/sync/';
const PROFILE_URL = '/profile/';
const USER_URL = '/user/';

const DEFAULT_PAGE_SIZE = 50;

export interface UserSyncResponse {
  user: SyncUser;
  build_date: string;
  commit_sha: string;
  version: string;
  permissions: UserRolePermissions[];
}

export function userSync(): Promise<UserSyncResponse> {
  return api.post(
    SYNC_URL,
    {},
    {
      onUnauthenticated: () => {
        /* Prevent force logout on unauthenticated, cuz it means that user is already logged out */
      },
    },
  );
}

export const SYNC_USER_QUERY = `${USER_URL}SYNC_USER_QUERY`;

export const useSyncUserQuery = (options?: RQUseQueryOptions<UserSyncResponse>) => {
  return useQuery<UserSyncResponse, ApiError>([SYNC_USER_QUERY], userSync, options);
};

export function getUser(userId: string, schoolId?: string): Promise<SyncUser> {
  // TODO: `school_id` query parameter should filter relations of the user in the provided School
  const params = {
    school_id: schoolId,
  };

  return api.get(USER_URL + userId, { params });
}

export const GET_USER_QUERY = `${USER_URL}GET_USER_QUERY`;

type GetUserQueryParams = { userId: string; schoolId?: string };

export const useGetUserQuery = (
  params: GetUserQueryParams,
  options?: RQUseQueryOptions<SyncUser | undefined>,
) => {
  return useQuery<SyncUser | undefined, ApiError>(
    [GET_USER_QUERY, params.userId, params],
    () => getUser(params.userId, params.schoolId),
    options,
  );
};

export type CreateUnverifiedEmailParams = {
  given_name?: string;
  last_name?: string;
  email: string;
  country?: number | null;
  password?: string;
  verify?: boolean;
};

export function createUnverifiedEmail({
  given_name,
  last_name,
  email,
  country,
  verify,
}: CreateUnverifiedEmailParams): Promise<CreateUnverifiedEmailResponse> {
  return api.post(createUnverifiedEmailPath(), {
    given_name,
    last_name,
    email,
    verify,
    country: country ?? undefined,
  });
}

export const useCreateUnverifiedEmailMutation = (
  options?: RQUseMutationOptions<CreateUnverifiedEmailResponse, CreateUnverifiedEmailParams>,
): RQUseMutationResult<CreateUnverifiedEmailResponse, CreateUnverifiedEmailParams> => {
  return useMutation(createUnverifiedEmail, options);
};

export function createUnverifiedEmailPublic(
  params: CreateUnverifiedEmailPublicRequest,
): Promise<CreateUnverifiedEmailPublicResponse> {
  return api.post(createUnverifiedEmailPublicPath(), params);
}

export const useCreateUnverifiedEmailPublicMutation = (
  options?: RQUseMutationOptions<
    CreateUnverifiedEmailPublicResponse,
    CreateUnverifiedEmailPublicRequest
  >,
): RQUseMutationResult<CreateUnverifiedEmailPublicResponse, CreateUnverifiedEmailPublicRequest> => {
  return useMutation(createUnverifiedEmailPublic, options);
};

type UpdateSchoolUserParams = {
  schoolId: string;
  userId: string;
  update: User.UserPartialUpdate.RequestBody;
};

function updateSchoolUser({
  schoolId,
  update,
  userId,
}: UpdateSchoolUserParams): Promise<User.UserPartialUpdate.ResponseBody> {
  return api.patch(`${USER_URL}${userId}?school_id=${schoolId}`, update);
}

export const useUpdateSchoolUserMutation = (
  options?: RQUseMutationOptions<User.UserPartialUpdate.ResponseBody, UpdateSchoolUserParams>,
): RQUseMutationResult<User.UserPartialUpdate.ResponseBody, UpdateSchoolUserParams> => {
  const queryClient = useQueryClient();

  return useMutation(updateSchoolUser, {
    ...options,
    onSuccess: (result, variables, ...data) => {
      options?.onSuccess?.(result, variables, ...data);
      queryClient.invalidateQueries([GET_USER_QUERY, variables.userId]);
    },
  });
};

export function listProfiles<T extends UserType>({
  schoolId,
  type,
  pageSize = DEFAULT_PAGE_SIZE,
  pageNumber,
  query,
  excludeSchoolUserTypes,
  lastNameForSuggestion,
  min = false,
  token,
}: ListProfileArguments<T>): Promise<PagedResponse<ListProfileReturn[T]>> {
  const params = removeObjectFalsyValues({
    page_size: pageSize,
    page_number: pageNumber,
    query,
    filter: type,
    school_id: schoolId,
    exclude_school_relation_types: excludeSchoolUserTypes?.join(','),
    last_name_for_suggestion: lastNameForSuggestion,
    min: Number(min),
  });

  return api.get(`${PROFILE_URL}list/`, { params, cancelToken: token });
}

export const GET_PROFILES_QUERY = `${PROFILE_URL}GET_PROFILES_QUERY`;

export const useGetProfilesQuery = <T extends UserType>(
  initialParams: ListProfileArguments<T>,
  options?: RQUseInfiniteQueryOptions<PagedResponse<ListProfileReturn[T]>>,
) => {
  const [params, setParams] = useState(initialParams);

  const query = useInfiniteQuery<PagedResponse<ListProfileReturn[T]>, ApiError>(
    [GET_PROFILES_QUERY, params],
    ({ pageParam }) => listProfiles({ pageNumber: pageParam, ...params }),
    {
      getNextPageParam: (lastPage) => {
        return !lastPage?.total_pages || lastPage?.current_page === lastPage?.total_pages
          ? undefined
          : lastPage.next_page;
      },
      getPreviousPageParam: (firstPage) => {
        return firstPage?.current_page ? firstPage?.previous_page : undefined;
      },
      ...options,
    },
  );

  return { ...query, setParams, params };
};

export function removeUser(userId: string, schoolId: string) {
  return api.remove(USER_URL + userId, { school_id: schoolId });
}

export function getEmploymentHistory(
  userId: string,
  schoolId: string,
): Promise<PagedResponse<EmploymentHistoryEntry>> {
  const params = {
    school_id: schoolId,
  };

  return api.get(`${USER_URL}${userId}/employment-history`, { params });
}

type AddFamilyParams = {
  schoolId: string;
  family: {
    adults: NewAdult[];
    children: NewFamilyMember[];
  };
};

type AddFamilyResponse = {
  adults: string[];
  children: string[];
  associations: string[];
};

export function addFamily(
  schoolId: AddFamilyParams['schoolId'],
  family: AddFamilyParams['family'],
): Promise<AddFamilyResponse> {
  // `profile_picture` is not allowed here
  const data = {
    school_id: schoolId,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    adults: family.adults.map(({ profile_picture, ...adult }) => ({
      school_id: schoolId,
      ...adult,
    })),
    children: family.children.map((child) => ({
      school_id: schoolId,
      ...child,
    })),
  };

  return api.post(`${PROFILE_URL}add-family`, data);
}

export const useAddFamilyMutation = (
  options?: RQUseMutationOptions<AddFamilyResponse, AddFamilyParams>,
): RQUseMutationResult<AddFamilyResponse, AddFamilyParams> => {
  return useMutation(
    (params: AddFamilyParams) => addFamily(params.schoolId, params.family),
    options,
  );
};

export function editFamily(
  schoolId: string,
  child_id: string,
  adults: NewFamilyMember[],
): Promise<{
  adults: string[];
  child_id: string[];
  associations: string[];
}> {
  const data = {
    school_id: schoolId,
    adults,
    child_id,
  };

  return api.patch(`${PROFILE_URL}edit-family`, data);
}

export function verifyEmailSignUp(
  email: string,
  verificationCode: string,
  password: string,
): Promise<boolean> {
  const params = {
    email,
    verification_code: verificationCode,
    password,
  };

  return api.post(`${USER_URL}verify-email-signup`, params);
}

export function acceptInvite(inviteId: string): Promise<string> {
  const params = {
    invite_id: encodeBase64(inviteId),
  };

  return api.post(`${USER_URL}accept-invite`, params);
}

export function verifyEmailForUsername(email: string, verificationCode: string): Promise<string> {
  const params = {
    email,
    verification_code: verificationCode,
  };

  return api.post(`${USER_URL}verify-email-for-username`, params);
}

export function verifyEmail(params: VerifyEmailRequest): Promise<VerifyEmailResponse> {
  return api.post(verifyEmailPath(), params);
}

export const useVerifyEmailMutation = (
  options?: RQUseMutationOptions<VerifyEmailResponse, VerifyEmailRequest>,
): RQUseMutationResult<VerifyEmailResponse, VerifyEmailRequest> => {
  return useMutation(verifyEmail, options);
};

export function createUnverifiedEmailForUsername(email?: StringOrNull): Promise<boolean> {
  const params = {
    email,
  };

  return api.post(`${USER_URL}create-unverified-email-for-username`, params);
}

export function verifyContactEmail(email: string, verificationCode: string): Promise<string> {
  const params = {
    email,
    verification_code: verificationCode,
  };

  return api.post(`${USER_URL}verify-contact-email`, params);
}

export function createContactEmail(email?: StringOrNull): Promise<boolean> {
  const params = {
    email,
  };

  return api.post(`${USER_URL}set-contact-email`, params);
}

export function requestPasswordReset(email: string): Promise<string> {
  const params = {
    email,
  };

  return api.post(`${USER_URL}create-password-reset-token`, params);
}

export function checkResetToken(token: string, email: string) {
  const params = {
    token,
    email,
  };

  return api.get(`${USER_URL}check-password-reset-token`, { params });
}

export function updateUserPassword(token: string, email: string, password: string) {
  const params = {
    token,
    email,
    password,
  };
  return api.patch(`${USER_URL}update-user-password`, params);
}

export function checkUserHealth(): Promise<{ ok: boolean }> {
  return api.get(`${USER_URL}healthcheck`);
}

export interface IGetFilters {
  schoolId: string;
  relationId: string;
  section?: FilterSection;
  defaultOnly?: boolean;
}

export interface IDeleteFilters {
  id: string;
  schoolId: string;
  relationId: string;
}

export function getFilter(id: string): Promise<SavedFilter> {
  return api.get(`${USER_URL}filters/${id}`);
}

export const getFilters = ({
  schoolId,
  relationId,
  section,
  defaultOnly,
}: IGetFilters): Promise<SavedFilter[]> => {
  const params = {
    school_id: schoolId,
    relation_id: relationId,
    default_only: defaultOnly,
    section,
  };

  return api.get(`${USER_URL}filters`, { params });
};

export const GET_FILTER_QUERY = `${USER_URL}GET_FILTER_QUERY`;

export const useGetFilterQuery = (id: string, options?: RQUseQueryOptions<SavedFilter>) => {
  return useQuery<SavedFilter, ApiError>([GET_FILTER_QUERY, id], () => getFilter(id), options);
};
export const GET_FILTERS_QUERY = `${USER_URL}GET_FILTERS_QUERY`;

export const useGetFiltersQuery = (
  params: IGetFilters,
  options?: RQUseQueryOptions<SavedFilter[]>,
) => {
  return useQuery<SavedFilter[], ApiError>(
    [GET_FILTERS_QUERY, params],
    async () => {
      const result = await getFilters(params);

      if (params.section === FilterSection.PayableFees) {
        return result.map((f) => ({
          ...f,
          filter: {
            ...f.filter,
            [FilterKeys.InvoicesAgeGroup]: f.filter[FilterKeys.AgeGroup],
            [FilterKeys.ProductType]: ProductTypeId.fromArrayOfAny(
              f.filter[FilterKeys.ProductType],
            ),
          },
        }));
      }

      return result;
    },
    options,
  );
};

const prepareCreateUpdateFilter = (filter: CreateFilterMutationParams['filter']) => {
  return {
    ...filter,
    show_by_present_absent: filter.show_by_present_absent || undefined,
    [FilterKeys.OnlyEmptyEmail]: undefined, // unsupported filter
    [FilterKeys.ProductType]: filter?.[FilterKeys.ProductType]?.map((t) => t.toString()),
    [FilterKeys.InvoicesAgeGroup]: undefined, // unsupported filter, replace with AgeGroup
    [FilterKeys.Expired]: undefined, // unsupported filter
    [FilterKeys.AgeGroup]: filter?.[FilterKeys.AgeGroup] || filter?.[FilterKeys.InvoicesAgeGroup],
    // BE requires this filter to be defined :/
    [FilterKeys.Date]: filter.date || [],
    conduct_select_types: filter.conduct_select_types?.length
      ? filter.conduct_select_types.map((option) => ({
          conduct_type_id: option.conduct_type_id,
          conduct_type_options: option.conduct_type_options,
        }))
      : undefined,
  };
};

export const createFilter = ({
  schoolId,
  relationId,
  section,
  name,
  filter,
  is_default,
}: CreateFilterMutationParams): Promise<SaveFilterResponse> => {
  const params = {
    relation_id: relationId,
    section,
    name,
    is_default,
    filter: prepareCreateUpdateFilter(filter),
  };

  return api.post(`${USER_URL}filters?school_id=${schoolId}`, params);
};

export type CreateFilterMutationParams = Omit<UpdateFilterMutationParams, 'id'>;

export const useCreateFilterMutation = (
  options?: RQUseMutationOptions<SaveFilterResponse, CreateFilterMutationParams>,
): RQUseMutationResult<SaveFilterResponse, CreateFilterMutationParams> => {
  const queryClient = useQueryClient();

  return useMutation(createFilter, {
    ...options,
    onSuccess: (result, variables, ...data) => {
      options?.onSuccess?.(result, variables, ...data);
      queryClient.invalidateQueries([GET_FILTERS_QUERY]);
    },
  });
};

export const updateFilter = ({
  id,
  schoolId,
  section,
  name,
  is_default,
  filter,
}: UpdateFilterMutationParams): Promise<SaveFilterResponse> => {
  const params = {
    section,
    name,
    is_default,
    filter: prepareCreateUpdateFilter(filter),
  };

  return api.patch(`${USER_URL}filters/${id}?school_id=${schoolId}`, params);
};

type UpdateFilterMutationParams = {
  schoolId: string;
  relationId: string;
  section: FilterSection;
} & SavedFilter;

export const useUpdateFilterMutation = (
  options?: RQUseMutationOptions<SaveFilterResponse, UpdateFilterMutationParams>,
): RQUseMutationResult<SaveFilterResponse, UpdateFilterMutationParams> => {
  const queryClient = useQueryClient();

  return useMutation(
    (params: UpdateFilterMutationParams) => {
      return updateFilter(params);
    },
    {
      ...options,
      onSuccess: (result, variables, ...data) => {
        options?.onSuccess?.(result, variables, ...data);
        queryClient.invalidateQueries([GET_FILTERS_QUERY]);
      },
    },
  );
};

export const removeFilter = ({ id, schoolId, relationId }: IDeleteFilters) => {
  const params = {
    relation_id: relationId,
  };

  return api.remove(`${USER_URL}filters/${id}?school_id=${schoolId}`, params);
};

export const useRemoveFilterMutation = (
  options?: RQUseMutationOptions<any, IDeleteFilters>,
): RQUseMutationResult<any, IDeleteFilters> => {
  const queryClient = useQueryClient();

  return useMutation((params: IDeleteFilters) => removeFilter(params), {
    ...options,
    onSuccess: (result, variables, ...data) => {
      options?.onSuccess?.(result, variables, ...data);
      queryClient.invalidateQueries([GET_FILTERS_QUERY]);
    },
  });
};

const closeUserAccount = (id: string): Promise<{ success: string }> => {
  return api.remove(`${USER_URL}${id}/account`);
};

export const useCloseUserAccountMutation = (
  options?: RQUseMutationOptions<{ success: string }, string>,
): RQUseMutationResult<{ success: string }, string> => {
  return useMutation(closeUserAccount, options);
};

type PostFeedbackParams = Partial<{
  source: string;
  email: string;
  name: string;
  role: string;
  organisation: string;
  question: string;
  country: string;
  phoneNumber: string;
  id: string;
  school_name: string;
}>;

export const postFeedback = (params: PostFeedbackParams): Promise<{ success: string }> => {
  return api.post(`${PROFILE_URL}feedback`, {
    ...params,
    date: format(new Date(), DEFAULT_DATE_FORMAT_FNS),
  });
};

export const usePostFeedbackMutation = (
  options?: RQUseMutationOptions<any, PostFeedbackParams>,
): RQUseMutationResult<any, PostFeedbackParams> => {
  return useMutation(postFeedback, options);
};

const pictureCache = new Map<string, any>();

// Note: We use a separate cache for images rather than React Query's cache since React Query has size limitations
// that could cause issues when caching many large images. This avoids memory issues while still providing caching benefits.
export const useGetPrivateImage = (url?: string | null) => {
  const [isFetching, setIsFetching] = useState(false);
  const cachedImageData = url ? pictureCache.get(url) : undefined;
  const hasCachedImage = !!cachedImageData;

  useEffect(() => {
    if (!url || hasCachedImage) {
      return;
    }

    setIsFetching(true);
    api
      .getPrivateImage(url, {
        onUnauthenticated: () => {
          /* Prevent force logout on unauthenticated in case of 401 error */
        },
      })
      .then((data) => {
        pictureCache.set(url, data);
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, [url, hasCachedImage]);

  if (!url) {
    return { data: undefined, isFetching: false };
  }

  if (cachedImageData) {
    return { data: cachedImageData, isFetching: false };
  }

  return { data: undefined, isFetching };
};

export const exportStudents = ({
  schoolId,
  filters,
  include,
  custom_field_ids,
  sort = [
    { columnTextId: 'last_name', direction: SORT_DIRECTION.ASC },
    { columnTextId: 'given_name', direction: SORT_DIRECTION.ASC },
  ],
}: ExportStudentsParams): Promise<ArrayBuffer> => {
  const queryParams: ExportStudentsQuery = removeObjectUndefinedNullValues({
    date: filters?.date?.[0],
    status_ids: filters?.status?.join(','),
    age_group: filters?.age_group?.join(','),
    house: filters?.house?.join(','),
    gender: filters?.gender?.join(','),
    nationality: filters?.nationality?.join(','),
    [FilterKeys.MedicalNeeds]: filters?.[FilterKeys.MedicalNeeds],
    [FilterKeys.CustomFields]: filters?.[FilterKeys.CustomFields]
      ? JSON.stringify(filters?.[FilterKeys.CustomFields])
      : undefined,
    include: include.join(','),
    custom_field_ids: custom_field_ids?.join(','),
    sort_by: getSortParams(sort),
  });

  return api.get(`${SCHOOLY_API_V2_URL}${getExportStudentsPath({ school_id: schoolId })}`, {
    responseType: 'arraybuffer',
    headers: { 'Content-Type': 'application/json', Accept: 'application/csv' },
    params: queryParams,
  });
};

export const useExportStudents = (
  options?: RQUseMutationOptions<ArrayBuffer, ExportStudentsParams>,
): RQUseMutationResult<ArrayBuffer, ExportStudentsParams> => {
  return useMutation(
    (params) =>
      exportStudents({ ...params, filters: removeObjectEmptyArrayValues(params.filters ?? {}) }),
    options,
  );
};

const checkUserEmailInUse = (
  params: CheckUserEmailInUseForSchoolRequest,
): Promise<CheckUserEmailInUseForSchoolResponse> => {
  const { schoolId, email } = params;
  return api.post(emailInUseForSchoolPath({ school_id: schoolId }), {}, { params: { email } });
};

export const useCheckUserEmailInUseMutation = (
  options?: RQUseMutationOptions<
    CheckUserEmailInUseForSchoolResponse,
    CheckUserEmailInUseForSchoolRequest
  >,
): RQUseMutationResult<
  CheckUserEmailInUseForSchoolResponse,
  CheckUserEmailInUseForSchoolRequest
> => {
  return useMutation(checkUserEmailInUse, { ...options });
};

const checkUserEmailInUsePublic = (
  params: CheckUserEmailInUsePublicRequestQuery,
): Promise<CheckUserEmailInUsePublicResponse> => {
  return api.post(emailInUsePublicPath(), {}, { params });
};

export const useCheckUserEmailInUsePublicMutation = (
  options?: RQUseMutationOptions<
    CheckUserEmailInUsePublicResponse,
    CheckUserEmailInUsePublicRequest
  >,
): RQUseMutationResult<CheckUserEmailInUsePublicResponse, CheckUserEmailInUsePublicRequest> => {
  return useMutation(checkUserEmailInUsePublic, { ...options });
};

export function userParent(): Promise<UserParentResponse> {
  return api.post(userParentPath(), {});
}

export const PARENT_USER_QUERY = `${USER_URL}PARENT_USER_QUERY`;

export const useParentUserQuery = (options?: RQUseQueryOptions<UserParentResponse>) => {
  return useQuery<UserParentResponse, ApiError>([PARENT_USER_QUERY], userParent, options);
};
